/** Opleiders-cockpit: /api/opleider/cockpit/* en /api/opleider/team/* (migratie 150). */
import api from './api';
import type { Conversation } from './chatService';
import type { AcademyTraining } from './medewerkerApi';
import type { OpleiderAcademySessie } from './opleidingenApi';
import type { Training } from '../types';
import type { OpleiderRol } from '../components/opleiderCockpit/opleiderMenu';

const d = <T,>(r: { data: { data: T } }) => r.data.data;

// ─── Menu 2 ──────────────────────────────────────────────────────────────────
export interface RosterTraining {
  id: number;
  titel: string;
  soort_opleiding: string | null;
  is_published: boolean;
  omslagfoto_url: string | null;
  datum_tijd: string | null;
  wanneer: string | null;
  locaties: string | null;
  aanmeldingen: number;
  nieuw: number;
  capaciteit: number;
  geboekt: number;
  sessies: number;
  vulgraad: number | null;
  markten: { medewerkers: boolean; professionals: boolean; bedrijven: boolean };
}
export const getRoster = () => api.get<{ data: RosterTraining[]; rol: OpleiderRol }>('/opleider/cockpit/trainingen').then((r) => r.data);

export interface Deelnemer {
  id: number;
  user_id: string;
  status: string;
  aangemeld_at: string;
  bron: 'medewerker' | 'professional';
  naam: string;
  email: string | null;
  telefoon: string | null;
  registratienummer: string | null;
  sessie_id: number | null;
  /** Presentie: null = nog niet vastgelegd */
  aanwezig?: boolean | null;
  aanwezigheid_bevestigd_op?: string | null;
  /** PE-punten die op het Beroepsregistratiecertificaat zijn geboekt (alleen professionals) */
  pe_punten?: number | null;
}
export interface PresentieSessie { id: number; naam: string | null; start_at: string | null }
export interface PresentieTraining {
  id: number;
  titel: string;
  pe_punten?: number | null;
  pe_register?: string | null;
  /** ISO; presentie bevestigen kan pas na het einde van de laatste les */
  laatste_les_eind?: string | null;
}
export const getPresentie = (trainingId: number) =>
  api.get<{ data: { training: PresentieTraining; deelnemers: Deelnemer[]; sessies: PresentieSessie[] } }>(`/opleider/cockpit/trainingen/${trainingId}/presentie`).then(d);

/** Presentie bevestigen: boekt PE-punten op het Beroepsregistratiecertificaat (409 als de laatste les nog niet voorbij is). */
export interface PresentieResultaat { bijgewerkt: number; pe_geboekt: number; vervallen: number }
export const bevestigPresentie = (trainingId: number, registraties: Array<{ id: number; aanwezig: boolean }>) =>
  api.post<{ data: PresentieResultaat }>(`/opleider/cockpit/trainingen/${trainingId}/presentie`, { registraties }).then(d);

/** Bulk-Broadcast: groepsbericht → losse 1-op-1 threads van alle cursisten (optioneel één sessie). */
export const stuurBroadcast = (trainingId: number, bericht: string, sessieId: number | null) =>
  api.post<{ data: { verzonden: number } }>(`/opleider/cockpit/trainingen/${trainingId}/broadcast`, { bericht, sessie_id: sessieId }).then(d);

export const startGroepschat = (trainingId: number) =>
  api.post<{ data: { conversation: Conversation; deelnemers: number } }>(`/opleider/cockpit/trainingen/${trainingId}/groepschat`).then(d);

export interface PreviewData {
  tegel: AcademyTraining;
  detail: Training;
  sessies: OpleiderAcademySessie[];
  reviews: { aantal: number; balken: { relevantie: number; toepasbaarheid: number; expertise: number } | null };
}
export const getPreview = (trainingId: number) => api.get<{ data: PreviewData }>(`/opleider/cockpit/preview/${trainingId}`).then(d);

// ─── Menu 3 · Data & Quality ─────────────────────────────────────────────────
export type Periode = 'maand' | 'kwartaal' | 'jaar';
export type As2Variant = 'toepasbaarheid' | 'welzijn';
interface PeriodeInfo { key: Periode; van: string; tot: string }

export interface MacroMetrics {
  periode: PeriodeInfo;
  boekingsvolume: { totaal: number; vorige_periode: number; per_markt: Record<Markt, number> };
  vulgraad: { pct: number | null; geboekt: number; capaciteit: number; sessies: number };
  kwaliteit: PlatformScore;
}
export interface PlatformScore {
  score: number | null;
  aantal: number;
  drempel: number;
  leaderboard: { positie: number; deelnemers: number } | null;
}
export const getMacro = (periode: Periode) =>
  api.get<{ data: MacroMetrics }>('/opleider/cockpit/analytics/macro', { params: { periode } }).then(d);

export interface MarktCijfers {
  volume: number;
  omzet: number;
  gem_boekingswaarde: number | null;
  annulering_pct: number | null;
  /** e_learning | workshop | meerdaags | incompany | bulk_seats (licenties tellen als bulk_seats) */
  primaire_leervorm: string | null;
}
export interface Demografie {
  periode: PeriodeInfo;
  labels: Markt[];
  volumes: number[];
  revenues: number[];
  markten: Record<Markt, MarktCijfers>;
}
export const getDemografie = (periode: Periode) =>
  api.get<{ data: Demografie }>('/opleider/cockpit/analytics/demographics', { params: { periode } }).then(d);

export interface Assen { inhoud: number; impact: number; structuur: number | null; gemiddelde: number }
export interface ReviewAssen {
  drempel: number;
  platform: PlatformScore;
  totaal: { aantal: number; aanbevelers: number; assen: Assen | null };
  trainingen: Array<{ id: number; titel: string; aantal: number; aanbevelers: number; as2_variant: As2Variant; allow_display: boolean; assen: Assen | null }>;
}
export const getReviewAssen = () => api.get<{ data: ReviewAssen }>('/opleider/cockpit/reviews').then(d);

export interface FeedbackItem {
  id: number;
  training_id: number;
  titel: string;
  tekst: string;
  geschoond: number;
  engine: 'llm' | 'regex' | null;
  markt: Markt;
  aanbeveling: number | null;
  periode: string;
}
export interface FeedbackFeed {
  drempel: number;
  trainingen: Array<{ id: number; titel: string; aantal: number; in_screening: number; allow_display: boolean }>;
  feed: FeedbackItem[];
}
export const getFeedback = () => api.get<{ data: FeedbackFeed }>('/opleider/cockpit/feedback').then(d);

// ─── Menu 4 ──────────────────────────────────────────────────────────────────
export type Markt = 'medewerkers' | 'professionals' | 'bedrijven';
export type FinanceStatus = 'gegenereerd' | 'voldaan' | 'verstreken' | 'geannuleerd' | 'gecrediteerd';
export interface LedgerRegel {
  id: string;
  bron: 'medewerker_boeking' | 'professional_factuur' | 'b2b_offerte' | 'legacy' | 'creditnota';
  markt: Markt;
  datum: string;
  verkoop_factuurnummer: string;
  fee_factuurnummer: string | null;
  omschrijving: string;
  /** Platform-klantnummer werkgever (BC-…) */
  klantnummer: string | null;
  /** Eenmalig transactie-ID van de boeking (TRX-…) — nooit een naam of vast medewerkernummer */
  medewerker_referentie: string | null;
  debiteur: string | null;
  marktprijs: number;
  activeringswaarde: number;
  fee: number;
  netto: number;
  openstaand: number;
  status: FinanceStatus;
  vervaldatum: string | null;
  uitbetaald: boolean;
  pdf: boolean;
  /** Gecrediteerde factuur: nummer van de creditnota */
  creditnota_nummer?: string | null;
  /** Creditnota: de factuur die hij opheft */
  crediteert_factuurnummer?: string | null;
}
export const getLedger = () => api.get<{ data: { regels: LedgerRegel[] } }>('/opleider/cockpit/finance/ledger').then(d);

export interface FinanceBalances {
  maand: string;
  omzet_lopende_maand: number;
  omzet_lopende_maand_netto: number;
  transacties_lopende_maand: number;
  beschikbaar_voor_uitbetaling: number;
  beschikbaar_aantal: number;
  openstaand_saldo: number;
  openstaand_aantal: number;
  verstreken_saldo: number;
  verstreken_aantal: number;
  bijgewerkt_op: string;
}
export const getBalances = () => api.get<{ data: FinanceBalances }>('/opleider/cockpit/finance/balances').then(d);

export interface CashflowMaand { maand: string; bruto: number; netto: number; fee: number; aantal: number }
export const getCashflow = (maanden = 12) =>
  api.get<{ data: CashflowMaand[] }>('/opleider/cockpit/finance/cashflow', { params: { maanden } }).then(d);

export type FactuurSoort = 'verkoopfactuur' | 'fee-factuur';
export const downloadFactuurPdf = async (regel: LedgerRegel, soort: FactuurSoort) => {
  const r = await api.get(`/opleider/cockpit/finance/ledger/${regel.id}/${soort}.pdf`, { responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${soort === 'verkoopfactuur' ? regel.verkoop_factuurnummer : regel.fee_factuurnummer}.pdf`;
  a.click();
  URL.revokeObjectURL(url);
};

export interface FeeMaand {
  maand: string;
  aantal: number;
  /** Creditnota's (kosteloze annulering) in deze maand; bedragen zijn al verrekend */
  creditnotas: number;
  bruto: number;
  fee: number;
  btw: number;
  fee_incl_btw: number;
  medewerkers: number;
  professionals: number;
  bedrijven: number;
}
export const getFees = () => api.get<{ data: { platformfee_pct: number; btw_pct: number; maanden: FeeMaand[] } }>('/opleider/cockpit/finance/fees').then(d);
export const downloadFeesCsv = async () => {
  const r = await api.get('/opleider/cockpit/finance/fees', { params: { format: 'csv' }, responseType: 'blob' });
  const url = URL.createObjectURL(r.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'belagom-platformfee-btw.csv';
  a.click();
  URL.revokeObjectURL(url);
};

// ─── Menu 5 ──────────────────────────────────────────────────────────────────
export interface ZakelijkContact {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  bedrijfsnaam: string | null;
  training_titel: string;
  rfq_status: string;
  rfq_id: string;
  functie: string | null;
  contact_type: 'opleider_employer';
  conversation_id?: string;
  last_message?: string | null;
  last_message_at?: string | null;
  unread?: number;
}
export const getZakelijkeContacten = () => api.get<{ data: ZakelijkContact[] }>('/opleider/cockpit/zakelijke-contacten').then(d);

// ─── Teambeheer ──────────────────────────────────────────────────────────────
export interface TeamLid {
  id: string | null;
  user_id: string;
  naam: string;
  email: string;
  rol: OpleiderRol;
  is_hoofdeigenaar: boolean;
  status: 'actief' | 'uitgenodigd' | 'verlopen' | 'inactief';
  accepted_at: string | null;
  invite_token_expires: string | null;
  trainingen: Array<{ id: number; titel: string }>;
  created_at: string | null;
}
export interface TeamOverzicht {
  leden: TeamLid[];
  trainingen: Array<{ id: number; titel: string; is_published: boolean }>;
  rollen: OpleiderRol[];
}
export const getTeam = () => api.get<{ data: TeamOverzicht }>('/opleider/team').then(d);
export const nodigTeamlidUit = (invoer: { naam: string; email: string; rol: OpleiderRol; training_ids?: number[] }) =>
  api.post<{ data: { lid_id: string } }>('/opleider/team/invite', invoer).then(d);
export const wijzigTeamlid = (id: string, invoer: { rol?: OpleiderRol; training_ids?: number[]; is_active?: boolean }) =>
  api.patch(`/opleider/team/${id}`, invoer).then((r) => r.data);
export const verwijderTeamlid = (id: string) => api.delete(`/opleider/team/${id}`).then((r) => r.data);
